import { alpha, type Theme } from '@mui/material/styles';

/** Opaque sticky table header background (must not show body rows when scrolling). */
export function tableHeadSurface(theme: Theme): string {
  return theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.grey[50];
}

/** Official column header tint — opaque in dark mode for sticky header row. */
export function officialColumnHeadSurface(theme: Theme): string {
  return theme.palette.mode === 'dark' ? '#1c2a1c' : 'rgba(139,195,74,0.08)';
}

/** Subtle elevated surface (legend rows, nested panels). */
export function subtleSurface(theme: Theme): string {
  return theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : theme.palette.grey[50];
}

/** Sticky column shadow tuned for palette mode. */
export function stickyColumnShadow(theme: Theme): string {
  return theme.palette.mode === 'dark'
    ? '2px 0 6px rgba(0,0,0,0.45)'
    : '2px 0 4px rgba(0,0,0,0.06)';
}

/** Semantic success/error text on tinted chip backgrounds. */
export function statusTextColor(theme: Theme, tone: 'success' | 'error'): string {
  if (theme.palette.mode === 'dark') {
    return tone === 'success' ? theme.palette.success.light : theme.palette.error.light;
  }
  return tone === 'success' ? theme.palette.success.dark : theme.palette.error.dark;
}

/** Full-screen review player shell — follows global light/dark unless forceDark. */
export function proModeSurfaces(theme: Theme, options?: { forceDark?: boolean }) {
  const isDark = options?.forceDark === true || theme.palette.mode === 'dark';
  return {
    isDark,
    rootBg: isDark ? '#121212' : theme.palette.background.default,
    headerBg: isDark ? '#1a1a1a' : theme.palette.background.paper,
    panelBg: isDark ? '#1e1e1e' : theme.palette.grey[50],
    timelineBg: isDark ? '#1a1a1a' : theme.palette.background.paper,
    border: isDark ? 'rgba(255,255,255,0.15)' : theme.palette.divider,
    cardBorder: isDark ? 'rgba(255,255,255,0.18)' : alpha(theme.palette.divider, 1),
    cardBg: isDark ? 'rgba(255,255,255,0.05)' : theme.palette.background.paper,
    cardSelectedBg: isDark
      ? 'rgba(139,195,74,0.12)'
      : alpha(theme.palette.secondary.main, 0.1),
    iconMuted: isDark ? 'rgba(255,255,255,0.85)' : theme.palette.text.secondary,
  };
}

/** App bar foreground when background is role-branded (not palette.primary.main). */
export function appBarForeground(theme: Theme, isReferee: boolean): string {
  if (isReferee) return '#ffffff';
  return theme.palette.primary.contrastText;
}

/** Visible scrollbars for analytics panels and nested lists. */
export function panelScrollbarSx(theme: Theme) {
  const isDark = theme.palette.mode === 'dark';
  const track = isDark ? '#151b22' : theme.palette.grey[100];
  const thumb = isDark
    ? alpha(theme.palette.secondary.main, 0.45)
    : alpha(theme.palette.text.primary, 0.28);
  const thumbHover = isDark
    ? alpha(theme.palette.secondary.main, 0.65)
    : alpha(theme.palette.text.primary, 0.42);
  return {
    scrollbarWidth: 'thin',
    scrollbarColor: `${thumb} ${track}`,
    '&::-webkit-scrollbar': { width: 10, height: 10 },
    '&::-webkit-scrollbar-track': { background: track, borderRadius: 5 },
    '&::-webkit-scrollbar-thumb': {
      background: thumb,
      borderRadius: 5,
      border: `2px solid ${track}`,
    },
    '&::-webkit-scrollbar-thumb:hover': { background: thumbHover },
    '&::-webkit-scrollbar-corner': { background: track },
  } as const;
}

/** Dark scrollbars for nested overflow regions (e.g. pro-mode timeline track). */
export function darkScrollbarSx(theme: Theme, options?: { forceDark?: boolean }) {
  const isDark = options?.forceDark === true || theme.palette.mode === 'dark';
  if (!isDark) return {};
  const track = '#151b22';
  const thumb = alpha(theme.palette.secondary.main, 0.45);
  const thumbHover = alpha(theme.palette.secondary.main, 0.65);
  return {
    scrollbarWidth: 'thin',
    scrollbarColor: `${thumb} ${track}`,
    '&::-webkit-scrollbar': { width: 8, height: 8 },
    '&::-webkit-scrollbar-track': { background: track },
    '&::-webkit-scrollbar-thumb': { background: thumb, borderRadius: 4 },
    '&::-webkit-scrollbar-thumb:hover': { background: thumbHover },
    '&::-webkit-scrollbar-corner': { background: track },
  } as const;
}
